import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Settings, Loader2, Palette, Zap, HelpCircle, ExternalLink, ShieldCheck, UserX, AlertTriangle, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { themes } from '../../themes';
import { TONES, ATTRIBUTIONS } from '../../data/constants';
import authService from '../../services/authService';

const SettingsDialog = ({ open, onOpenChange, variant }) => {
    const isHub = variant === 'hub';
    const { user, updateProfile, theme, setTheme, logout } = useAuth();
    const navigate = useNavigate();
    const [localTheme, setLocalTheme] = useState(theme);
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showCancelSubscription, setShowCancelSubscription] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [localTone, setLocalTone] = useState(user?.preferred_tone || 'Neutral / Authentic');
    const [localAttribution, setLocalAttribution] = useState(user?.attribution_preference || 'none');

    useEffect(() => {
        if (user && open) {
            setLocalTheme(theme);
            setLocalTone(user.preferred_tone || 'Neutral / Authentic');
            setLocalAttribution(user.attribution_preference || 'none');
        }
    }, [user, open, theme]);

    const handleSave = async () => {
        setLoading(true);
        // Persist the theme choice to the backend as well
        const success = await updateProfile({ 
            preferred_theme: localTheme,
            preferred_tone: localTone,
            attribution_preference: localAttribution,
            has_set_initial_theme: true // Ensure auto-detect doesn't overwrite manual choice
        });
        
        if (success) {
            setTheme(localTheme);
            onOpenChange(false);
        }
        setLoading(false);
    };

    const handleNavigate = (path) => {
        onOpenChange(false);
        navigate(path);
    };

    const handleCancelSubscription = async () => {
        setCancelLoading(true);
        try {
            // finding D-01(a): this used to read localStorage under the key
            // 'token', which is never written anywhere — the real key is
            // 'auth_token' (see apiClient.js). That sent every request here as
            // "Authorization: Bearer null". Routing through apiClient (via
            // authService) fixes the key lookup and the cross-origin cookie
            // (SameSite=Lax) in one move.
            await authService.cancelSubscription();

            // Refresh user data to show updated plan
            window.location.reload();
        } catch (error) {
            // apiClient's global interceptor already surfaces a toast for this.
            console.error('Cancel subscription error:', error);
        } finally {
            setCancelLoading(false);
            setShowCancelSubscription(false);
        }
    };

    const handleDowngrade = () => {
        setShowCancelSubscription(false);
        onOpenChange(false);
        navigate('/subscription');
    };

    const handleDeleteAccount = async () => {
        if (!showDeleteConfirm) {
            setShowDeleteConfirm(true);
            return;
        }

        setDeleteLoading(true);
        try {
            // finding D-01(a): same wrong-localStorage-key bug as cancel above.
            await authService.deleteAccount();

            // Logout and redirect to login page
            await logout();
            onOpenChange(false);
            // finding D-19: the registered route is /auth/login, not /login.
            navigate('/auth/login');
        } catch (error) {
            // apiClient's global interceptor already surfaces a toast for this.
            console.error('Delete account error:', error);
        } finally {
            setDeleteLoading(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl p-0 overflow-hidden gap-0">
                <DialogTitle className="sr-only">Settings</DialogTitle>
                <DialogDescription className="sr-only">
                    Manage your application settings, appearance, and subscription.
                </DialogDescription>
                <div className="flex flex-col sm:flex-row h-[80vh] sm:h-[550px]">
                    {/* Left Sidebar */}
                    <div className="w-full sm:w-56 bg-muted/30 border-b sm:border-r border-border sm:p-4 flex flex-1 flex-col z-10 shrink-0 p-2 overflow-hidden min-h-0">
                        <div className="hidden sm:flex items-center gap-2 mb-8 px-2">
                            <Settings className="w-5 h-5 text-primary" />
                            <h2 className="font-bold text-lg">Settings</h2>
                        </div>

                        <Tabs defaultValue="appearance" orientation="vertical" className="w-full flex-1 flex flex-col sm:block min-h-0">
                            <TabsList className="flex flex-row sm:flex-col h-auto bg-transparent gap-1 p-0 overflow-x-auto scrollbar-hide pb-1 sm:pb-0">
                                <TabsTrigger
                                    value="appearance"
                                    className="w-full justify-start gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm px-2 sm:px-4"
                                >
                                    <Palette className="w-4 h-4 shrink-0" />
                                    <span className="hidden sm:inline">Appearance</span>
                                    <span className="sm:hidden text-xs">Appear.</span>
                                </TabsTrigger>
                                <TabsTrigger
                                    value="preferences"
                                    className="w-full justify-start gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm px-2 sm:px-4"
                                >
                                    <Settings className="w-4 h-4 shrink-0" />
                                    <span className="hidden sm:inline">Preferences</span>
                                    <span className="sm:hidden text-xs">Pref.</span>
                                </TabsTrigger>
                                <TabsTrigger
                                    value="subscription"
                                    className="w-full justify-start gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm px-2 sm:px-4"
                                >
                                    <Zap className="w-4 h-4 shrink-0" />
                                    <span className="hidden sm:inline">Subscription</span>
                                    <span className="sm:hidden text-xs">Plan</span>
                                </TabsTrigger>
                                <TabsTrigger
                                    value="support"
                                    className="w-full justify-start gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm px-2 sm:px-4"
                                >
                                    <HelpCircle className="w-4 h-4 shrink-0" />
                                    <span className="hidden sm:inline">Support</span>
                                    <span className="sm:hidden text-xs">Help</span>
                                </TabsTrigger>
                                <TabsTrigger
                                    value="account"
                                    className="w-full justify-start gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm px-2 sm:px-4"
                                >
                                    <UserX className="w-4 h-4 shrink-0" />
                                    <span className="hidden sm:inline">Account</span>
                                    <span className="sm:hidden text-xs">Acc.</span>
                                </TabsTrigger>
                            </TabsList>

                            {/* Main Content Area */}
                            <div className="flex-1 flex flex-col min-h-0 bg-background relative sm:absolute sm:left-56 sm:top-0 sm:right-0 sm:bottom-0 overflow-hidden">
                                <div className="flex-1 p-6 sm:p-8 overflow-y-auto">
                                    <TabsContent value="appearance" className="mt-0 space-y-6">
                                        <div>
                                            <h3 className="text-lg font-bold mb-1">Appearance</h3>
                                            <p className="text-sm text-muted-foreground mb-6">Personalize your workspace look and feel.</p>

                                            <div className="space-y-4">
                                                <div className="space-y-3">
                                                    <Label>Active Theme</Label>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                        {Object.entries(themes).map(([key, themeObj]) => (
                                                            <button
                                                                key={key}
                                                                onClick={() => setLocalTheme(key)}
                                                                className={cn(
                                                                    "flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-200 group relative overflow-hidden",
                                                                    localTheme === key
                                                                        ? "border-primary bg-primary/5 shadow-md"
                                                                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                                                                )}
                                                            >
                                                                <div className={cn(
                                                                    "w-8 h-8 rounded-full border shadow-sm shrink-0 flex items-center justify-center",
                                                                    themeObj.previewStyle
                                                                )}>
                                                                    {localTheme === key && <Check className="w-4 h-4 text-white drop-shadow-md" />}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-bold leading-snug break-words">{themeObj.name}</p>
                                                                    <p className="text-[10px] text-muted-foreground truncate uppercase tracking-wider">{key}</p>
                                                                </div>

                                                                {/* Optional hover glow effect */}
                                                                <div className={cn(
                                                                    "absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500",
                                                                    themeObj.className.includes('dark') ? 'bg-white' : 'bg-black'
                                                                )} />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="preferences" className="mt-0 space-y-6 focus-visible:outline-none">
                                        <div>
                                            <h3 className="text-lg font-bold mb-1">Preferences</h3>
                                            <p className="text-sm text-muted-foreground mb-6">Default settings for your content generations.</p>
                                            {isHub && (
                                                <p className="text-xs text-muted-foreground mb-4 italic">
                                                    Managed from your main Dashboard.
                                                </p>
                                            )}

                                            <div className="space-y-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Preferred Tone</Label>
                                                    <Select
                                                        value={localTone}
                                                        onValueChange={setLocalTone}
                                                        disabled={isHub}
                                                    >
                                                        <SelectTrigger className="mt-1 h-auto min-h-[2.75rem] text-[13px] leading-snug py-2">
                                                            <SelectValue placeholder="Select tone" />
                                                        </SelectTrigger>
                                                        <SelectContent className="max-w-[var(--radix-select-trigger-width)] w-full">
                                                            {TONES.map(t => (
                                                                <SelectItem key={t.value} value={t.value} className="text-[13px] leading-snug py-2.5 whitespace-normal">
                                                                    {t.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Mention in Script</Label>
                                                    <Select
                                                        value={localAttribution}
                                                        onValueChange={setLocalAttribution}
                                                        disabled={isHub}
                                                    >
                                                        <SelectTrigger className="mt-1 h-11">
                                                            <SelectValue placeholder="Select attribution" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {ATTRIBUTIONS.map(attr => (
                                                                <SelectItem key={attr.value} value={attr.value}>
                                                                    {attr.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="subscription" className="mt-0 space-y-6">
                                        <div>
                                            <h3 className="text-lg font-bold mb-1">Subscription</h3>
                                            <p className="text-sm text-muted-foreground mb-6">Manage your tier and billing details.</p>
                                            {isHub && (
                                                <p className="text-xs text-muted-foreground mb-4 italic">
                                                    Managed from your main Dashboard.
                                                </p>
                                            )}

                                            <Card className="border-2 border-dashed bg-accent/20">
                                                <CardContent className="p-8 flex flex-col items-center text-center gap-4">
                                                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner">
                                                        <Zap className="w-8 h-8" />
                                                    </div>
                                                    <div>
                                                        <p className="text-xl font-bold">{user?.plan_tier} Tier</p>
                                                        <p className="text-sm text-muted-foreground">You are currently on the {user?.plan_tier} plan.</p>
                                                    </div>
                                                    <Button
                                                        className="w-full h-11 font-bold"
                                                        onClick={() => handleNavigate('/subscription')}
                                                        disabled={isHub}
                                                    >
                                                        Manage Plan & Billing
                                                    </Button>

                                                    {user?.plan_tier !== "Wanderer" && (
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            className="w-full h-9 mt-2"
                                                            onClick={() => setShowCancelSubscription(true)}
                                                            disabled={isHub}
                                                        >
                                                            Cancel Subscription
                                                        </Button>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Cancel Subscription Confirmation Dialog */}
                                            {!isHub && showCancelSubscription && (
                                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                                                    <Card className="w-full max-w-md mx-4 border-2 border-destructive/50">
                                                        <CardContent className="p-6 space-y-4">
                                                            <div className="flex items-start gap-3">
                                                                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600">
                                                                    <AlertTriangle className="w-6 h-6" />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h4 className="font-bold text-lg mb-1">Cancel Subscription?</h4>
                                                                    <p className="text-sm text-muted-foreground mb-4">
                                                                        You're about to cancel your subscription. Your account will be downgraded to the <strong>Wanderer</strong> tier at <strong>$0/month</strong>.
                                                                    </p>

                                                                    <div className="space-y-2">
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Not sure? You can also downgrade to a lower paid tier instead.
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="flex flex-col gap-2 pt-2">
                                                                <Button
                                                                    variant="destructive"
                                                                    onClick={handleCancelSubscription}
                                                                    disabled={cancelLoading}
                                                                    className="w-full"
                                                                >
                                                                    {cancelLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                                    {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    onClick={handleDowngrade}
                                                                    disabled={cancelLoading}
                                                                    className="w-full"
                                                                >
                                                                    Downgrade Instead
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    onClick={() => setShowCancelSubscription(false)}
                                                                    disabled={cancelLoading}
                                                                    className="w-full"
                                                                >
                                                                    Keep Current Plan
                                                                </Button>
                                                            </div>
                                                        </CardContent>
                                                    </Card>
                                                </div>
                                            )}
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="support" className="mt-0 space-y-6">
                                        <div>
                                            <h3 className="text-lg font-bold mb-1">Support & Documentation</h3>
                                            <p className="text-sm text-muted-foreground mb-6">Need help? We've got you covered.</p>

                                            <div className="grid grid-cols-1 gap-3">
                                                <Button
                                                    variant="outline"
                                                    className="h-12 justify-between px-4 group"
                                                    onClick={() => handleNavigate('/help')}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <HelpCircle className="w-5 h-5 text-indigo-500" />
                                                        <span className="font-semibold">Help Center & Docs</span>
                                                    </div>
                                                    <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </Button>

                                                <Button
                                                    variant="outline"
                                                    className="h-12 justify-between px-4 group"
                                                    asChild
                                                >
                                                    <a href="/terms" target="_blank" rel="noreferrer">
                                                        <div className="flex items-center gap-3">
                                                            <ShieldCheck className="w-5 h-5 text-emerald-500" />
                                                            <span className="font-semibold">Terms of Service</span>
                                                        </div>
                                                        <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </a>
                                                </Button>
                                            </div>
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="account" className="mt-0 space-y-6">
                                        <div>
                                            <h3 className="text-lg font-bold mb-1">Account Settings</h3>
                                            <p className="text-sm text-muted-foreground mb-6">Manage your account and data.</p>

                                            {!isHub && (
                                            <Card className="border-border bg-muted/30 mb-6">
                                                <CardContent className="p-6 flex items-center justify-between gap-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600">
                                                            <HelpCircle className="w-5 h-5" />
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-sm">Sandbox Tutorial</h4>
                                                            <p className="text-xs text-muted-foreground">
                                                                {user?.has_completed_tutorial
                                                                    ? "You've completed the tutorial."
                                                                    : "You haven't completed the tutorial yet."}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => {
                                                            updateProfile({ has_completed_tutorial: false, has_completed_onboarding: false });
                                                            onOpenChange(false);
                                                        }}
                                                        disabled={!user?.has_completed_tutorial && !user?.has_completed_onboarding}
                                                        className="h-9 px-4 rounded-xl"
                                                    >
                                                        Reset Tutorial
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                            )}

                                            <Card className="border-2 border-destructive/50 bg-destructive/5">
                                                <CardContent className="p-6 space-y-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center text-destructive">
                                                            <AlertTriangle className="w-6 h-6" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="font-bold text-base mb-1">Delete Account</h4>
                                                            <p className="text-sm text-muted-foreground mb-4">
                                                                Permanently delete your account and all associated data.
                                                                Your subscription will be automatically cancelled and set to the Wanderer tier.
                                                            </p>

                                                            {!showDeleteConfirm ? (
                                                                <Button
                                                                    variant="destructive"
                                                                    onClick={() => setShowDeleteConfirm(true)}
                                                                    disabled={deleteLoading}
                                                                    className="w-full"
                                                                >
                                                                    <UserX className="w-4 h-4 mr-2" />
                                                                    Delete My Account
                                                                </Button>
                                                            ) : (
                                                                <div className="space-y-3">
                                                                    <div className="p-3 bg-background rounded-lg border border-destructive">
                                                                        <p className="text-sm font-semibold text-destructive mb-1">
                                                                            Are you absolutely sure?
                                                                        </p>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            This action cannot be undone. All your data will be permanently deleted.
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        <Button
                                                                            variant="outline"
                                                                            onClick={() => setShowDeleteConfirm(false)}
                                                                            disabled={deleteLoading}
                                                                            className="flex-1"
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                        <Button
                                                                            variant="destructive"
                                                                            onClick={handleDeleteAccount}
                                                                            disabled={deleteLoading}
                                                                            className="flex-1"
                                                                        >
                                                                            {deleteLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                                            {deleteLoading ? 'Deleting...' : 'Yes, Delete Forever'}
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </TabsContent>
                                </div>

                                <div className="p-4 border-t bg-muted/10 flex justify-end gap-2">
                                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                                    <Button onClick={handleSave} disabled={loading} className="px-8">
                                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Save Settings
                                    </Button>
                                </div>
                            </div>
                        </Tabs>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SettingsDialog;
